/**
 * GA4 analytics with a consent gate.
 *
 * Privacy-first: nothing from Google loads until the visitor explicitly
 * accepts. Until then (and after a decline) zero Google scripts, cookies, or
 * requests happen. The choice is remembered in localStorage so the banner
 * only appears once.
 *
 * The player site is a client-routed SPA with no router library, so we patch
 * history.pushState / replaceState (plus popstate) to send a GA4 page_view on
 * every in-app navigation. Auto page_view is disabled to avoid double counts.
 *
 * GA4_ID is a public Measurement ID (it ships in the client bundle), not a
 * secret. Until it is set to a real G-XXXXXXXXXX value the whole feature is
 * inert: the banner never shows and no analytics load.
 */

// TODO: replace with the real GA4 Measurement ID from the GA4 admin
// (Admin -> Data streams -> Web -> Measurement ID). Same value as the Payload
// public ConsentBanner.
export const GA4_ID = 'G-BSHG5TDYGT'

const CONSENT_KEY = 'dzm_analytics_consent'
type Consent = 'granted' | 'denied'

export function isConfigured(): boolean {
  return /^G-[A-Z0-9]{6,}$/.test(GA4_ID)
}

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

function storeConsent(v: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, v)
  } catch {
    /* private mode / storage disabled: best-effort only */
  }
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let loaded = false
let routeHooked = false

function loadGtag() {
  if (loaded || !isConfigured()) return
  loaded = true

  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(s)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  // We send page_view manually on route changes; anonymize IP for privacy.
  window.gtag('config', GA4_ID, { send_page_view: false, anonymize_ip: true })

  trackPageView()
  installRouteTracking()
}

function installRouteTracking() {
  if (routeHooked) return
  routeHooked = true
  const fire = () => trackPageView()
  ;(['pushState', 'replaceState'] as const).forEach((k) => {
    const orig = history[k]
    history[k] = function (this: History, ...args: unknown[]) {
      const r = orig.apply(this, args as Parameters<History['pushState']>)
      fire()
      return r
    } as History[typeof k]
  })
  window.addEventListener('popstate', fire)
}

// Never let a one-time auth secret reach GA: strip sensitive query params before
// reporting the page location/path (a ?reset=<token> can be in the URL on landing).
const SENSITIVE_QS = ['reset', 'token', 'verify']
function scrubbedUrl(): { path: string; href: string } {
  try {
    const u = new URL(location.href)
    SENSITIVE_QS.forEach((k) => u.searchParams.delete(k))
    return { path: u.pathname + u.search, href: u.origin + u.pathname + u.search }
  } catch {
    return { path: location.pathname, href: location.origin + location.pathname }
  }
}

export function trackPageView() {
  if (typeof window.gtag !== 'function') return
  const { path, href } = scrubbedUrl()
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: href,
    page_title: document.title,
  })
}

/** Call once on app start. Loads analytics only if consent was already given. */
export function initAnalytics() {
  if (getConsent() === 'granted') loadGtag()
}

export function grantConsent() {
  storeConsent('granted')
  loadGtag()
}

export function denyConsent() {
  storeConsent('denied')
}
